/**
 * "Shared with you" (ADR-026): restricted arches the viewer can use — through
 * a grant to them, a workspace or Company they belong to, or a guild.
 * Restricted arches never appear in the store or search, so this is where a
 * grantee finds them. Company-owned ones say which Company they come from.
 *
 * Renders nothing when there is nothing shared (or the viewer is signed out).
 */

import { Link } from 'react-router-dom';
import { archePath } from '@/lib/arche-url';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/auth/hooks/useAuth';
import { useArchesSharedWithMe } from '@/auth/hooks/useArcheGovernance';

export function SharedWithYouSection() {
  const { isAuthenticated, isGuest } = useAuth();
  const shared = useArchesSharedWithMe({ enabled: isAuthenticated && !isGuest });
  if (shared.arches.length === 0) return null;
  return (
    <section className="py-8" aria-label="Shared with you">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <h2 className="mb-4 text-2xl font-bold text-foreground">Shared with you</h2>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {shared.arches.map((a) => (
            <Card key={a.archId} className="p-4">
              <Link
                to={`/a/${encodeURIComponent(archePath(a))}`}
                className="font-medium text-foreground hover:underline"
              >
                {a.name}
              </Link>
              {a.fromCompany && (
                <p className="text-xs text-muted-foreground">
                  From {a.fromCompany.name ?? 'a Company'}
                </p>
              )}
              {a.simpleDescription && (
                <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                  {a.simpleDescription}
                </p>
              )}
            </Card>
          ))}
        </div>
        {shared.hasMore && (
          <Button
            variant="outline"
            size="sm"
            className="mt-4"
            disabled={shared.isFetchingMore}
            onClick={shared.loadMore}
          >
            Show more
          </Button>
        )}
      </div>
    </section>
  );
}
